import {
  ECompanyType,
  EDisbursementMethod,
  EPaySchedule,
  EProductType,
  schemes,
  updateUserOnboardingSettingsSchema,
  zSchema,
} from "@zeal/common";

export const getUserInfoResponseSchema = zSchema.object({
  account_number: zSchema.string(),
  address: zSchema.string(),
  address_line2: zSchema.string(),
  appIntegrations: zSchema.object({
    quickbooks: zSchema.object({
      bankAccount: zSchema.object({ Name: zSchema.string() }),
      enabled: zSchema.boolean(),
      mapping: zSchema.object({
        doubletime: zSchema.object({ Name: zSchema.string() }),
        employerTaxes: zSchema.object({ Name: zSchema.string() }),
        flat: zSchema.object({ Name: zSchema.string() }),
        hourly: zSchema.object({ Name: zSchema.string() }),
        overtime: zSchema.object({ Name: zSchema.string() }),
        piecework: zSchema.null(),
        pto: zSchema.object({ Name: zSchema.string() }),
        reimbursement: zSchema.object({ Name: zSchema.string() }),
        tips: zSchema.object({ Name: zSchema.string() }),
        vacation: zSchema.object({ Name: zSchema.string() }),
      }),
      realmId: zSchema.string(),
    }),
  }),
  approval_checks_no: zSchema.number(),
  business_address: zSchema.string(),
  business_city: zSchema.string(),
  business_ein: zSchema.string(),
  business_name: zSchema.string(),
  business_phone: zSchema.string(),
  business_state: zSchema.string(),
  business_zip: zSchema.string(),
  check_created_webhook: zSchema.string(),
  check_processed_webhook: zSchema.string(),
  city: zSchema.string(),
  classification: zSchema.enum(["PRODUCTION", "TEST"]),
  company_id: schemes.uuid(),
  company_type: zSchema.nativeEnum(ECompanyType),
  dob: schemes.dob(),
  product_type: zSchema.nativeEnum(EProductType),
  completed_paperwork_no: zSchema.number(),
  contractor_payment_webhook: zSchema.string(),
  contractor_webhook: zSchema.string(),
  customer_accounts_enabled: zSchema.boolean().optional(),
  default_pay_schedule: zSchema.string(),
  deposit_account_form: zSchema.boolean(),
  direct_deposit_form: zSchema.boolean(),
  electronic_communication_form: zSchema.boolean(),
  electronic_signature_form: zSchema.boolean(),
  email: zSchema.string(),
  employee_check_webhook: zSchema.string(),
  employee_webhook: zSchema.string(),
  fallback_disbursement_method: zSchema
    .nativeEnum(EDisbursementMethod)
    .optional(),
  feature_flags: zSchema.record(zSchema.boolean()),
  first_name: zSchema.string(),
  has_logo: zSchema.boolean(),
  has_uploaded_check: zSchema.boolean(),
  home_setup_mode: zSchema.literal(false),
  instant_pay_enabled: zSchema.boolean(),
  isMailingAddressDifferent: zSchema.literal(false),
  job_title: zSchema.string(),
  last_name: zSchema.string(),
  legal_structure: zSchema.string(),
  live_key: zSchema.string(),
  mail_address: zSchema.string(),
  mail_city: zSchema.string(),
  mail_state: zSchema.string(),
  mail_zip: zSchema.string(),
  not_onboarded_no: zSchema.number(),
  onboarding_settings: updateUserOnboardingSettingsSchema,
  onboarding_url: zSchema.string(),
  ownership_percentage: zSchema.number(),
  patriot_act_form: zSchema.boolean(),
  paycards_enabled: zSchema.boolean(),
  permissions: zSchema.object({
    canCreateChecks: zSchema.boolean(),
    canCreateWorker: zSchema.boolean(),
    canSubmitTaxInformation: zSchema.boolean(),
    customerAccountsEnabled: zSchema.boolean(),
    customPaperworkEnabled: zSchema.boolean(),
    instantPayEnabled: zSchema.boolean(),
    onboardingWorkerProfileOptional: zSchema.boolean(),
    paycardsEnabled: zSchema.boolean(),
    reserveEnabled: zSchema.boolean(),
    workerBankAccountsEnabled: zSchema.boolean(),
  }),
  profile_config: zSchema.object({ useTwoFactor: zSchema.literal(false) }),
  puzzl_terms_conditions: zSchema.boolean(),
  regular_payroll_firstDate: zSchema.string(),
  regular_payroll_firstPayPeriodID: zSchema.string(),
  regular_payroll_schedule: zSchema.nativeEnum(EPaySchedule),
  reporting_agent_form: zSchema.boolean(),
  reserve_enabled: zSchema.boolean(),
  routing_number: zSchema.string(),
  ssn: schemes.ssn(),
  state: schemes.jurisdiction(),
  status: zSchema.string(),
  tax_information_authorization_form: zSchema.boolean(),
  test_key: zSchema.string(),
  use_everify: zSchema.boolean().optional(),
  use_i9_admin_review: zSchema.boolean().optional(),
  use_skippable_bank_info: zSchema.boolean(),
  user_type: zSchema.literal("sole_co"),
  webhook_secret: zSchema.string(),
  white_label_id: zSchema.string(),
  whiteLabelID: schemes.uuid(),
  worker_created_webhook: zSchema.string(),
  worker_everify_webhook: zSchema.string(),
  worker_onboarding_webhook: zSchema.string(),
  worker_i9_webhook: zSchema.string(),
  worker_setup_mode: zSchema.literal(false),
  worker_shift_webhook: zSchema.string(),
  zip: zSchema.string(),
});

export type TGetUserInfoResponseSchema = zSchema.infer<
  typeof getUserInfoResponseSchema
>;
